import { useDisclosure } from "@chakra-ui/hooks";
import { Folder } from "domain/entities/folders.entities";
import {
  getFolderSeances,
  removeSeanceFromFolder,
} from "domain/folders/folders.actions";
import { selectFolderSeances } from "domain/folders/folders.selectors";
import { useEffect, useState } from "react";
import {
  HiArrowLeft,
  HiFolderOpen,
  HiOutlinePencil,
  HiOutlineTrash,
} from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "ui/hook/store";
import { ModalManageFolder } from "ui/pages/app/my-creations/components/ModalManageFolder";
import { translate } from "ui/i18n";
import { MySeanceCard } from "ui/pages/app/components/activities/seances/MySeanceCard";
import { Seance } from "domain/entities/seances.entities";
import { useWindowWidth } from "ui/utils/hooks";
import { INSkeleton } from "ui/pages/app/components/inputs/INSkeleton";
import { Snackbar } from "ui/pages/snackbar";
import { ModalDeleteFolder } from "ui/pages/app/my-creations/components/ModalDeleteFolder";
import { CustomTextTooltip } from "ui/pages/app/components/CustomTextTooltip";

interface IProps {
  selectedFolder: Folder | null;
  setSelectedFolder: (folder: Folder | null) => void;
}

export function FolderPage({ selectedFolder, setSelectedFolder }: IProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isDesktop = useWindowWidth();
  const modalManageFolder = useDisclosure();
  const modalDeleteFolder = useDisclosure();
  const folderSeances = useAppSelector(selectFolderSeances);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (selectedFolder) {
      fetchInfos();
    }
  }, [selectedFolder]); //eslint-disable-line

  const fetchInfos = async () => {
    const { success } = await dispatch(
      getFolderSeances(Number(selectedFolder.id))
    ).unwrap();
    if (!success) navigate("/my-creations");
    setIsLoading(false);
  };

  const handleSeanceCardClick = async (seance: Seance) => {
    if (seance.active) {
      navigate(`/seances?id=${seance?.id}`);
    } else navigate("/create-seance", { state: { seanceId: seance.id } });
  };

  const handleRemoveFromFolder = async (folderSeanceId: number) => {
    const result = await dispatch(
      removeSeanceFromFolder({ folderSeanceId, folderId: selectedFolder.id })
    ).unwrap();
    if (result.success) {
      Snackbar.showSuccess({
        title: translate("ns1:Folders.RemovedFromFolder"),
      });
    }
  };

  return (
    <div className="flex h-full flex-col p-4 md:flex-row md:py-8 md:pl-6 md:pr-10">
      <div className="mb-4 flex w-full items-center justify-between md:my-0 md:w-12 md:items-start">
        <div className="flex w-full items-center justify-between gap-4">
          <button
            onClick={() => setSelectedFolder(null)}
            className="z-50 mt-0 flex h-5 items-center md:mt-2"
          >
            <HiArrowLeft size={24} className="text-darkGrey-800" />
          </button>
          {!isDesktop && (
            <div className="flex items-center gap-4">
              <button
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
                onClick={() => modalManageFolder.onOpen()}
              >
                <HiOutlinePencil className="text-secondaryGrey-900" size={20} />
              </button>
              <button
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
                onClick={() => modalDeleteFolder.onOpen()}
              >
                <HiOutlineTrash className="text-secondaryGrey-900" size={20} />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="w-full">
        <div className="flex w-full items-center justify-between">
          <h1 className="mt-4 w-full px-4 text-center text-base text-secondaryGrey-900 md:mt-0 md:px-0 md:text-left md:text-3xl">
            {selectedFolder.label}
          </h1>
          {isDesktop && (
            <div className="flex items-center gap-4">
              <CustomTextTooltip
                text={translate("ns1:Activities.Tooltips.Rename")}
              >
                <button
                  className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
                  onClick={() => modalManageFolder.onOpen()}
                >
                  <HiOutlinePencil
                    className="text-secondaryGrey-900"
                    size={20}
                  />
                </button>
              </CustomTextTooltip>
              <CustomTextTooltip
                text={translate("ns1:Activities.Tooltips.Delete")}
              >
                <button
                  className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
                  onClick={() => modalDeleteFolder.onOpen()}
                >
                  <HiOutlineTrash
                    className="text-secondaryGrey-900"
                    size={20}
                  />
                </button>
              </CustomTextTooltip>
            </div>
          )}
        </div>

        {/* Seances */}
        <h2 className="mt-10 ps-5 text-lg text-secondaryGrey-900 md:text-2xl">
          {translate("ns1:Folders.Seances")}
        </h2>
        {folderSeances && folderSeances.length > 0 ? (
          <div className="mb-8 mt-4 grid w-full grid-cols-2 gap-2 md:mt-8 md:gap-6 lg:grid-cols-3 xl:grid-cols-4">
            {!isLoading
              ? folderSeances.map((folderSeance, index) => (
                  <MySeanceCard
                    seance={folderSeance.seance}
                    key={"seance" + index}
                    onClick={() => handleSeanceCardClick(folderSeance.seance)}
                    isInFolder
                    removeFromFolder={() =>
                      handleRemoveFromFolder(folderSeance.id)
                    }
                  />
                ))
              : [...Array(12)].map((_, index) => (
                  <INSkeleton key={"seances-skeleton" + index} />
                ))}
          </div>
        ) : (
          <EmptyState type="seance" />
        )}
      </div>

      <ModalManageFolder
        isOpen={modalManageFolder.isOpen}
        onClose={modalManageFolder.onClose}
        folder={selectedFolder}
        setFolder={setSelectedFolder}
      />

      <ModalDeleteFolder
        isOpen={modalDeleteFolder.isOpen}
        onClose={modalDeleteFolder.onClose}
        folderId={selectedFolder.id}
        removeSelectedFolder={() => setSelectedFolder(null)}
      />
    </div>
  );
}

const EmptyState = ({ type }: { type: "seance" }) => {
  return (
    <div className="mb-8 mt-4 flex w-fit items-center space-x-4 rounded-[20px] border-2 border-darkGrey-200 px-4 py-5 md:mt-8">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-darkGrey-200">
        {type === "seance" && <HiFolderOpen size={24} color="#2B3674" />}
      </div>
      <span className="text-sm font-medium text-darkGrey-700">
        {translate(`ns1:Folders.NoAdded${type}`)}
      </span>
    </div>
  );
};
